import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import {
  Card, CardHeader, Col, Row, Table,
} from 'reactstrap';

import BadgeActive from 'src/components/Badge/BadgeActive';
import NotFoundError from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/NotFoundError';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import { useIntlCurrency } from 'src/util/i18n/currency';
import { formatDateTimeFull } from 'src/util/i18n/helpers';
import buildStandingChargeRate from 'src/util/i18n/tariff/standingCharge';

const AdminStandingChargeShowFragment = graphql`
  fragment AdminStandingChargeShow_standingCharge on StandingCharge @throwOnFieldError {
    uuid
    identifier
    externalIdentifier
    title
    description
    rate
    timeUnit
    createdAt
    updatedAt
    tariff {
      uuid
      title
      active { start finish }
    }
  }
`;

/**
 * Admin view of a single standing charge and its tariff. Field errors throw to
 * the route's PlaceholderErrorBoundary.
 * @param {object} props - Component props.
 * @param {object|null} props.standingCharge - Relay fragment reference for the standing charge.
 * @returns {React.ReactElement} AdminStandingChargeShow component.
 */
function AdminStandingChargeShow({ standingCharge: standingChargeRef = null }) {
  const standingCharge = useFragment(AdminStandingChargeShowFragment, standingChargeRef);
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  if (!standingCharge) {
    // A completed query without a standing charge would otherwise never leave the spinner.
    throw new NotFoundError('AdminStandingChargeShow: the query returned no standing charge');
  }

  const {
    uuid, identifier, externalIdentifier, title, description, rate, timeUnit, createdAt, updatedAt,
    tariff,
  } = standingCharge;

  // Page title and meta information
  const pageTitle = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_show.page.title',
    defaultMessage: 'Enosi Admin - Standing Charge - {title}',
  }, { title });

  // Breadcrumb labels
  const breadcrumbAdmin = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_show.breadcrumbs.admin.label',
    defaultMessage: 'Admin',
  });
  const breadcrumbStandingCharges = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_show.breadcrumbs.standing_charges.label',
    defaultMessage: 'Standing Charges',
  });

  // Card header
  const cardHeaderDetails = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_show.card.header.details.label',
    defaultMessage: 'Standing Charge details',
  });

  // Table labels
  const tableLabelId = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_show.table.label.id.label',
    defaultMessage: 'ID',
  });
  const tableLabelExternalIdentifier = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_show.table.label.external_identifier.label',
    defaultMessage: 'External identifier',
  });
  const tableLabelExternalIdentifierShort = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_show.table.label.external_identifier.short.label',
    defaultMessage: 'Ext ID',
  });
  const tableLabelTitle = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_show.table.label.title.label',
    defaultMessage: 'Title',
  });
  const tableLabelIdentifier = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_show.table.label.identifier.label',
    defaultMessage: 'Identifier',
  });
  const tableLabelDescription = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_show.table.label.description.label',
    defaultMessage: 'Description',
  });
  const tableLabelRate = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_show.table.label.rate.label',
    defaultMessage: 'Rate',
  });
  const tableLabelStandingUnit = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_show.table.label.standing_unit.label',
    defaultMessage: 'Standing unit',
  });
  const tableLabelTariff = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_show.table.label.tariff.label',
    defaultMessage: 'Tariff',
  });
  const tableLabelCreatedAt = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_show.table.label.created_at.label',
    defaultMessage: 'Created at',
  });
  const tableLabelUpdatedAt = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_show.table.label.updated_at.label',
    defaultMessage: 'Updated at',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbStandingCharges, path: '/admin/standing-charges' },
          { name: title },
        ]}
      />

      <h1>
        {title}
      </h1>

      <Row className="clear-both">
        <Col xs={12} sm={6} md={4}>
          <Card className="mb-4">
            <CardHeader>
              {cardHeaderDetails}
            </CardHeader>
            <Table responsive>
              <tbody>
                <tr>
                  <th scope="row" className="text-right">{tableLabelId}</th>
                  <td>
                    <UUID uuid={uuid} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">
                    <abbr
                      title={tableLabelExternalIdentifier}
                    >
                      {tableLabelExternalIdentifierShort}
                    </abbr>
                  </th>
                  <td>{externalIdentifier}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableLabelTitle}</th>
                  <td>{title}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableLabelIdentifier}</th>
                  <td>{identifier}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableLabelDescription}</th>
                  <td>{description}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableLabelRate}</th>
                  <td>
                    {buildStandingChargeRate(intl, intlCurrency, timeUnit, rate)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableLabelStandingUnit}</th>
                  <td>{timeUnit}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableLabelTariff}</th>
                  <td>
                    {tariff && (
                      <>
                        <Link to={`/admin/tariffs/${tariff.uuid}`}>
                          <strong>{tariff.title}</strong>
                        </Link>
                        <br />
                        <UUID uuid={tariff.uuid} />
                        <br />
                        <BadgeActive start={tariff.active.start} finish={tariff.active.finish} />
                      </>
                    )}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableLabelCreatedAt}</th>
                  <td>
                    {createdAt
                      && formatDateTimeFull(intl, createdAt)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableLabelUpdatedAt}</th>
                  <td>
                    {updatedAt
                      && formatDateTimeFull(intl, updatedAt)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>
    </>
  );
}

AdminStandingChargeShow.propTypes = {
  standingCharge: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default AdminStandingChargeShow;
